import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  formaDialogScoalaOcupata,
  infoDialogScoalaOcupata,
  textContactCerereInAsteptare,
  type StatusInscriereScoala,
} from '../utils/scoala-ocupata';
import { CONTACT_ECOTIC, hrefTelefon } from '@/shared/utils/contact-ecotic';
import { telefonAfisat } from '@/shared/utils/telefon-national';
import { RandContactEcotic } from './RandContactEcotic';

interface ScoalaOcupataDialogProps {
  status: StatusInscriereScoala | null | undefined;
  numeProfesor: string | null | undefined;
  /** Golirea campului de cod SIIIR -- mecanica exacta a "alege alta scoala" (decizie F1). */
  onAlegeAltaScoala: () => void;
  /** «Depune totuși cererea» (doar pentru scoala deja aprobata): deschide formularul strans. */
  onDepuneTotusi: () => void;
}

/**
 * Dialog INFORMATIV pentru ambele stari (din 18.09 nicio stare nu mai blocheaza din frontend —
 * `APROBATA_EDITIA_CURENTA` era «blocant» pana atunci, iar backendul o primeste acum ca cerere de
 * preluare). `EDITIE_ANTERIOARA` NU trece pe-aici -- ramane panoul inline din pagina.
 *
 *  - `APROBATA_EDITIA_CURENTA`: cele doua recomandari + contactul echipei, «Alege altă școală» sau
 *    «Depune totuși cererea».
 *  - `IN_ASTEPTARE_EDITIA_CURENTA`: cine a trimis cererea + persoana de contact (din 07.10, in
 *    locul lui «echipa ECOTIC decide»), «Continuă oricum» / «Alege altă școală».
 *
 * Se deschide o singura data per (status, profesor) -- nu la fiecare randare cat timp
 * raspunsul ramane acelasi, altfel un dialog inchis de utilizator s-ar redeschide singur.
 */
export function ScoalaOcupataDialog({
  status,
  numeProfesor,
  onAlegeAltaScoala,
  onDepuneTotusi,
}: ScoalaOcupataDialogProps) {
  const [open, setOpen] = useState(false);
  const ultimaCheieRef = useRef<string | undefined>(undefined);
  const forma = formaDialogScoalaOcupata(status);

  useEffect(() => {
    const cheieCurenta = forma ? `${status}|${numeProfesor ?? ''}` : undefined;
    if (cheieCurenta && cheieCurenta !== ultimaCheieRef.current) {
      setOpen(true);
    }
    ultimaCheieRef.current = cheieCurenta;
  }, [status, numeProfesor, forma]);

  if (!forma) return null;

  const aprobata = status === 'APROBATA_EDITIA_CURENTA';
  const { titlu, mesaj } = infoDialogScoalaOcupata(
    status as 'APROBATA_EDITIA_CURENTA' | 'IN_ASTEPTARE_EDITIA_CURENTA',
    numeProfesor
  );

  const alegeAltaScoala = () => {
    setOpen(false);
    onAlegeAltaScoala();
  };

  const continua = () => {
    setOpen(false);
    if (aprobata) onDepuneTotusi();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titlu}</DialogTitle>
          <DialogDescription>{mesaj}</DialogDescription>
          {aprobata ? (
            <RandContactEcotic className="m-0 text-sm" />
          ) : (
            <ContactCerereInAsteptare />
          )}
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={continua}>
            {aprobata ? 'Depune totuși cererea' : 'Continuă oricum'}
          </Button>
          <Button type="button" onClick={alegeAltaScoala}>
            Alege altă școală
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * «Contactați <nume> - responsabil proiect - <telefon> pentru informații suplimentare» — textul
 * clientului, cu numele in bold si telefonul ca link `tel:` (afisat national, grupat 4-3-3).
 * Nimic, cand variabilele de build sunt goale.
 */
function ContactCerereInAsteptare() {
  const text = textContactCerereInAsteptare(CONTACT_ECOTIC);
  if (!text) return null;
  const href = text.telefon ? hrefTelefon(text.telefon) : '';
  const telefon = telefonAfisat(text.telefon);

  return (
    <p className="m-0 text-sm" data-contact-ecotic>
      {text.inainte}
      {text.nume && <strong>{text.nume}</strong>}
      {text.mijloc}
      {telefon &&
        (href ? (
          /* Linkul sta IN propozitie: `inline` cu padding vertical ii da tinta de 44px fara sa
             inalte rândul (paddingul unui element inline nu intra in inaltimea liniei). Cu
             `inline-flex` + min-height, randurile ieseau distantate inegal. */
          <a href={href} className="whitespace-nowrap py-3.5 underline">
            {telefon}
          </a>
        ) : (
          telefon
        ))}
      {text.dupa}
    </p>
  );
}
